/**
 * PIQ 9.0: Compact Verdict Bar (Decision Compression)
 * ====================================================
 * Height <=72px, neutral styling.
 * - Left: posture badge (Hold/Monitor/Act/Clear)
 * - Center: headline (state-led) + Value Ledger impact line
 * - Right: State + Belief chips + "Why?" button
 * 
 * PIQ 9.0 Task 5: Value Ledger as emotional anchor
 */

import React from 'react';
import {
  CheckCircle,
  Eye,
  Zap,
  AlertTriangle,
  Brain,
  Activity,
  HelpCircle,
  TrendingUp,
  Shield,
} from 'lucide-react';
import type { BrainContractSubset, PostureType } from './operatorViewAdapter';

// ============================================================
// Types
// ============================================================

interface VerdictBarProps {
  brainContract: BrainContractSubset;
  onWhyClick?: () => void;
}

// ============================================================
// Helpers
// ============================================================

function getRetailerLabel(key: string): string {
  const labels: Record<string, string> = {
    ulta: 'Ulta',
    jcp: 'JCP',
    macys: "Macy's",
    nordstrom: 'Nordstrom',
    amazon: 'Amazon',
    shopify: 'Shopify',
  };
  return labels[key?.toLowerCase()] || key;
}

function formatPercent(val: number): string {
  return `${Math.round(val * 100)}%`;
}

interface VerdictConfig {
  label: string;
  icon: React.ReactNode;
  badgeClass: string;
  bgClass: string;
  borderClass: string;
}

function getVerdictConfig(posture: PostureType): VerdictConfig {
  switch (posture) {
    case 'action_required':
      return {
        label: 'Act',
        icon: <Zap className="w-3.5 h-3.5" />,
        badgeClass: 'bg-red-600 text-white',
        bgClass: 'bg-red-50/70',
        borderClass: 'border-red-200/80',
      };
    case 'all_clear':
      return {
        label: 'Clear',
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        badgeClass: 'bg-emerald-600 text-white',
        bgClass: 'bg-emerald-50/70',
        borderClass: 'border-emerald-200/80',
      };
    case 'monitor_only':
    default:
      return {
        label: 'Hold',
        icon: <Eye className="w-3.5 h-3.5" />,
        badgeClass: 'bg-gray-600 text-white',
        bgClass: 'bg-gray-50/80',
        borderClass: 'border-gray-200',
      };
  }
}

function computeHeadline(bc: BrainContractSubset): string {
  const posture = bc.decisionPosture?.posture || 'monitor_only';
  const primaryDriver = bc.retailerAttribution?.primaryDriver;
  
  if (posture === 'action_required') {
    if (primaryDriver) {
      return `${getRetailerLabel(primaryDriver.retailer)} driving unexpected movement`;
    }
    return 'Material action required';
  }
  
  if (posture === 'all_clear') {
    return 'No material issues detected';
  }
  
  // monitor_only
  const stateLabel = bc.businessState?.stateLabel || bc.businessState?.primaryState;
  if (stateLabel) {
    return `Holding — ${stateLabel.replace(/_/g, ' ')}`;
  }
  
  return 'Holding — no actions met thresholds';
}

// ============================================================
// Component
// ============================================================

/**
 * PIQ 9.0 Task 5: Format value ledger impact as compact line
 * One line, numbers only, no charts.
 */
function formatValueImpact(brainContract: BrainContractSubset): string | null {
  const ledger = brainContract.valueLedger;
  if (!ledger?.summaryMetrics) return null;
  
  const metrics = ledger.summaryMetrics;
  const parts: string[] = [];
  
  // Prevented downside
  if (metrics.preventedDownsideLow > 0 || metrics.preventedDownsideHigh > 0) {
    const avg = Math.round((metrics.preventedDownsideLow + metrics.preventedDownsideHigh) / 2);
    if (avg >= 1000) {
      parts.push(`$${(avg / 1000).toFixed(1)}K protected`);
    } else if (avg > 0) {
      parts.push(`$${avg} protected`);
    }
  }
  
  // Noise suppressed
  if (metrics.noiseReductionPct > 0.1) {
    parts.push(`${Math.round(metrics.noiseReductionPct * 100)}% noise filtered`);
  } else if (metrics.noiseSuppressed > 0) {
    parts.push(`${metrics.noiseSuppressed} alerts suppressed`);
  }
  
  // Realized impact
  if (metrics.realizedImpact > 0) {
    if (metrics.realizedImpact >= 1000) {
      parts.push(`$${(metrics.realizedImpact / 1000).toFixed(1)}K realized`);
    } else {
      parts.push(`$${Math.round(metrics.realizedImpact)} realized`);
    }
  }
  
  if (parts.length === 0) return null;
  return parts.join(' · ');
}

export function VerdictBar({ brainContract, onWhyClick }: VerdictBarProps) {
  const posture = brainContract.decisionPosture?.posture || 'monitor_only';
  const config = getVerdictConfig(posture);
  const headline = computeHeadline(brainContract);
  
  const stateLabel = brainContract.businessState?.stateLabel || brainContract.businessState?.primaryState;
  const beliefLabel = brainContract.beliefSummary?.primary?.replace(/_/g, ' ');
  
  // PIQ 9.0 Task 5: Value Ledger impact line
  const valueImpact = formatValueImpact(brainContract);

  return (
    <div className={`rounded-md border ${config.bgClass} ${config.borderClass} px-3 py-1.5`}>
      <div className="flex items-center justify-between gap-2">
        {/* Left: Badge + Headline + Value Impact */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${config.badgeClass}`}>
            {config.icon}
            {config.label}
          </span>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-700 truncate block">
              {headline}
            </span>
            {/* PIQ 9.0: This Week's Impact (Value Ledger) */}
            {valueImpact && (
              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                <Shield className="w-2.5 h-2.5 text-emerald-500" />
                {valueImpact}
              </span>
            )}
          </div>
        </div>

        {/* Right: State + Belief + Why */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* State chip */}
          {stateLabel && (
            <div className="hidden sm:flex items-center gap-1 px-1.5 py-0.5 bg-white/80 rounded border border-gray-200/80 text-[10px]">
              <Activity className="w-2.5 h-2.5 text-violet-500" />
              <span className="text-gray-600 font-medium">{stateLabel.replace(/_/g, ' ')}</span>
            </div>
          )}
          
          {/* Belief chip */}
          {beliefLabel && (
            <div className="hidden md:flex items-center gap-1 px-1.5 py-0.5 bg-white/80 rounded border border-gray-200/80 text-[10px]">
              <Brain className="w-2.5 h-2.5 text-indigo-500" />
              <span className="text-gray-600 font-medium">{beliefLabel}</span>
            </div>
          )}
          
          {/* Why button */}
          <button
            onClick={onWhyClick}
            className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700 bg-white/80 rounded border border-gray-200/80 hover:bg-gray-100 transition-colors"
          >
            <HelpCircle className="w-2.5 h-2.5" />
            Why?
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerdictBar;
